import { cachedGet, requestCache } from '@/utils/cache';
import { API_BASE_URL } from '@/config';

export interface AutoTradingNotification {
  id: string;
  reason: string;
  timestamp: string;
  risk_level: string;
}

export interface AutoTradingNotificationsResponse {
  notifications?: AutoTradingNotification[];
  [key: string]: unknown;
}

export interface AutoTradingStatusResponse {
  auto_trading_enabled?: boolean;
  paper_auto_trading_enabled?: boolean;
  paper_trading_mode?: boolean;
  [key: string]: unknown;
}

export interface AutoTradingToggleResponse extends AutoTradingStatusResponse {
  message?: string;
}

export interface SLTPStats {
  total_trades: number;
  recent_trades: number;
  learned_regimes: number;
  be_usage_rate: number;
  partial_tp_usage_rate: number;
  be_avg_profit: number;
  partial_tp_avg_profit: number;
  [key: string]: unknown;
}

export interface SLTPStatsResponse {
  success: boolean;
  stats?: SLTPStats;
  error?: string;
}

// Main API service object

interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}

const api = {
  async request(endpoint: string, options: RequestOptions = {}) {
    const token = localStorage.getItem('access_token');
    
    const config: RequestInit = {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      credentials: 'include',
      ...options,
    };

    if (options.body) {
      config.body = options.body;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
  },

  async get(endpoint: string) {
    const response = await this.request(endpoint);
    return {
      data: await response.json()
    };
  },

  async post(endpoint: string, data?: any) {
    const response = await this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
    return {
      data: await response.json()
    };
  },

  async put(endpoint: string, data?: any) {
    const response = await this.request(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
    return {
      data: await response.json()
    };
  },

  async delete(endpoint: string) {
    const response = await this.request(endpoint, {
      method: 'DELETE',
    });
    return {
      data: await response.json()
    };
  }
};

// Additional API functions - Direct fetch to avoid base URL issues
export const getAutoTradingNotifications = async (): Promise<AutoTradingNotificationsResponse> => {
  return cachedGet<AutoTradingNotificationsResponse>(`${API_BASE_URL}/api/trading/auto-trade/notifications`, { ttl: 15000 });
};

export const getAutoTradingStatus = async (): Promise<AutoTradingStatusResponse> => {
  return cachedGet<AutoTradingStatusResponse>(`${API_BASE_URL}/api/trading/auto-trade/status`, { ttl: 5000 });
};

export const toggleAutoTrading = async (enabled: boolean): Promise<AutoTradingToggleResponse> => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/api/trading/auto-trade/toggle`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({ enabled }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const formattedError = new Error(data?.message || 'Failed to toggle auto trading');
    (formattedError as any).errorCode = (data as any)?.error_code;
    (formattedError as any).currentBalance = (data as any)?.current_balance;
    (formattedError as any).minimumRequired = (data as any)?.minimum_required;
    (formattedError as any).responseData = data;
    throw formattedError;
  }

  return data as AutoTradingToggleResponse;
};

export const togglePaperAutoTrading = async (enabled: boolean): Promise<AutoTradingToggleResponse> => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/api/trading/paper-auto-trade/toggle`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({ enabled }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const formattedError = new Error(data?.message || 'Failed to toggle paper auto trading');
    (formattedError as any).errorCode = (data as any)?.error_code;
    (formattedError as any).currentBalance = (data as any)?.current_balance;
    (formattedError as any).minimumRequired = (data as any)?.minimum_required;
    (formattedError as any).responseData = data;
    throw formattedError;
  }

  return data as AutoTradingToggleResponse;
};

export const getSLTPStats = async (): Promise<SLTPStatsResponse> => {
  return cachedGet<SLTPStatsResponse>(`${API_BASE_URL}/api/trading/sltp-stats`, { ttl: 30000 });
};

// Live Trading TP/SL API Methods
export const setTakeProfit = async (positionId: string, takeProfitPrice: number): Promise<{ success: boolean; message: string }> => {
  const token = localStorage.getItem('access_token');
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/api/trading/positions/${positionId}/take-profit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify({ take_profit_price: takeProfitPrice }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to set take profit');
  }

  const result = await response.json();

  requestCache.invalidate('/api/trading/positions');
  requestCache.invalidate('/api/trading/sltp-stats');

  return result;
};

export const setStopLoss = async (positionId: string, stopLossPrice: number): Promise<{ success: boolean; message: string }> => {
  const token = localStorage.getItem('access_token');
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/api/trading/positions/${positionId}/stop-loss`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify({ stop_loss_price: stopLossPrice }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to set stop loss');
  }

  const result = await response.json();

  requestCache.invalidate('/api/trading/positions');
  requestCache.invalidate('/api/trading/sltp-stats');

  return result;
};

export interface CloseLivePositionResponse {
  success: boolean;
  trade_id?: string;
  symbol?: string;
  pnl?: number | { amount?: number };
  exit_price?: number;
  [key: string]: unknown;
}

export const closeLivePosition = async (positionId: string): Promise<CloseLivePositionResponse> => {
  const token = localStorage.getItem('access_token');

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/api/trading/positions/${positionId}/close`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to close live position');
  }

  const result: CloseLivePositionResponse = await response.json();

  requestCache.invalidate('/api/trading/positions');
  requestCache.invalidate('/api/trading/sltp-stats');
  requestCache.invalidate('/api/trading/active_position');

  return result;
};

export default api;
